import { padFlatNumber } from "../staffQuarterUtils";
import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import { useDispatch, useSelector } from "react-redux";
import { getOccupancyHistory } from "../../../../core/data/redux/staffQuarterSlice";
import { Spin } from "antd";
import dayjs from "dayjs";

/* ================= TYPES ================= */
interface HistoryRecord {
    key: string;
    empNo: string;
    name: string;
    location: string;
    block: string;
    flatNo: string;
    status: "Occupied" | "Vacated" | "Allotted";
    from: string;
    to: string;
    duration: string;
}

interface TableColumn<T> {
    title: string;
    key: string;
    className?: string;
    render?: (value: any, record: T, index: number) => React.ReactNode;
    sorter?: (a: T, b: T) => number;
}

/* ================= COMPONENTS ================= */
const DashboardTile = ({
    title,
    icon,
    count,
    link,
    colorClass = "text-primary",
}: {
    title: string;
    icon?: string;
    count?: string | number;
    link: string;
    colorClass?: string;
}) => {
    return (
        <div className="col-xxl-4 col-xl-4 col-lg-4 col-md-6 col-sm-6 mb-3">
            <div
                className="card h-100 border-0 shadow-sm hover-card"
                style={{
                    borderRadius: "16px",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    overflow: "hidden",
                    backgroundColor: "#fff"
                }}
            >
                <div className="card-body d-flex align-items-center p-3">
                    <div
                        className={`d-flex align-items-center justify-content-center me-3 rounded-circle`}
                        style={{
                            width: "48px",
                            height: "48px",
                            backgroundColor: "rgba(var(--bs-primary-rgb), 0.03)",
                            border: "1px solid rgba(0,0,0,0.03)"
                        }}
                    >
                        {icon && <i className={`ti ${icon} ${colorClass}`} style={{ fontSize: "24px" }}></i>}
                    </div>
                    <div>
                        <div className="text-muted small mb-1" style={{ fontSize: "12px", letterSpacing: "0.2px" }}>{title}</div>
                        <div className={`h4 fw-bold mb-0 ${colorClass}`}>{count}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const DashboardSection = ({ title, children }: { title: string; children: React.ReactNode }) => {
    return (
        <div className="mb-4">
            <h5 className="fw-bold text-secondary mb-3 ms-1" style={{ fontSize: "15px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {title}
            </h5>
            <div className="row">{children}</div>
        </div>
    );
};

/* ================= MAIN COMPONENT ================= */
const InventoryHistory = () => {
    const routes = all_routes;
    const dispatch = useDispatch<any>();

    const { occupancyHistory, loading } = useSelector((state: any) => state.staffQuarterState);
    const apiData = occupancyHistory?.data || [];

    const [searchText, setSearchText] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("All Statuses");
    const [selectedLocation, setSelectedLocation] = useState("All Locations");

    React.useEffect(() => {
        dispatch(getOccupancyHistory({}));
    }, [dispatch]);

    const data: HistoryRecord[] = useMemo(() => {
        return apiData.map((item: any) => ({
            key: String(item.id),
            empNo: item.employeeId || "-",
            name: item.employeeName || "-",
            location: item.location || "-",
            block: item.block || "-",
            flatNo: item.flatNo || "-",
            status: item.status?.charAt(0).toUpperCase() + item.status?.slice(1).toLowerCase() || "-",
            from: item.occupiedOn ? dayjs(item.occupiedOn.replace('Z', '')).format("YYYY-MM-DD hh:mm A") : "-",
            to: item.vacatedOn ? dayjs(item.vacatedOn.replace('Z', '')).format("YYYY-MM-DD hh:mm A") : "Present",
            duration: item.durationText || "-"
        }));
    }, [apiData]);

    /* ================= STATE & FILTERS ================= */

    // Dynamic Options
    const filterOptions = useMemo(() => {
        const statuses = Array.from(new Set(data.map(d => d.status))).sort();
        const locations = Array.from(new Set(data.map(d => d.location))).sort();
        return {
            statuses: ["All Statuses", ...statuses],
            locations: ["All Locations", ...locations]
        };
    }, [data]);

    // Filter Logic
    const filteredData = useMemo(() => {
        return data.filter(item => {
            const matchesSearch =
                item.empNo.toLowerCase().includes(searchText.toLowerCase()) ||
                item.name.toLowerCase().includes(searchText.toLowerCase()) ||
                item.location.toLowerCase().includes(searchText.toLowerCase()) ||
                item.flatNo.includes(searchText);
            const matchesStatus = selectedStatus === "All Statuses" || item.status === selectedStatus;
            const matchesLocation = selectedLocation === "All Locations" || item.location === selectedLocation;
            return matchesSearch && matchesStatus && matchesLocation;
        });
    }, [data, searchText, selectedStatus, selectedLocation]);

    // Stats Logic
    const stats = useMemo(() => ({
        total: data.length,
        occupied: data.filter(r => r.status === "Occupied").length,
        vacated: data.filter(r => r.status === "Vacated").length,
    }), [data]);

    const getValueByPath = (obj: any, path: string) => {
        return path.split('.').reduce((acc, part) => acc && acc[part], obj);
    };

    const getStatusBadgeClass = (status: string) => {
        switch (status) {
            case "Occupied": return "badge bg-success";
            case "Vacated": return "badge bg-secondary";
            case "Allotted": return "badge bg-primary";
            default: return "badge bg-light text-dark border";
        }
    };

    /* ================= CONFIGURATION ================= */
    const dashboardSections = [
        {
            title: "History Overview",
            tiles: [
                { title: "Total Records", icon: "ti-file-text", count: stats.total, link: "#", colorClass: "text-primary" },
                { title: "Currently Occupied", icon: "ti-user-check", count: stats.occupied, link: "#", colorClass: "text-success" },
                { title: "Vacated", icon: "ti-history", count: stats.vacated, link: "#", colorClass: "text-secondary" },
            ]
        }
    ];

    const columns: TableColumn<HistoryRecord>[] = [
        {
            title: "Employee",
            key: "name",
            render: (val, record) => (
                <div>
                    <div className="fw-bold text-dark">{val}</div>
                    <div className="text-muted small">{record.empNo}</div>
                </div>
            ),
            sorter: (a, b) => a.name.localeCompare(b.name)
        },
        {
            title: "Location",
            key: "location",
            sorter: (a, b) => a.location.localeCompare(b.location)
        },
        {
            title: "Block / Flat",
            key: "block",
            render: (_, record) => <span>{record.block}-{padFlatNumber(record.flatNo)}</span>
        },
        {
            title: "Status",
            key: "status",
            className: "text-center",
            render: (val) => <span className={getStatusBadgeClass(val)}>{val}</span>
        },
        {
            title: "Period",
            key: "from",
            render: (_, record) => (
                <div className="small">
                    <div>{record.from} <i className="ti ti-arrow-right mx-1 text-muted"></i> {record.to}</div>
                    <div className="text-muted text-xs">Duration: {record.duration}</div>
                </div>
            ),
            sorter: (a, b) => new Date(a.from).getTime() - new Date(b.from).getTime()
        },
    ];

    return (
        <div className="page-wrapper">
            {loading === "pending" && (
                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75" style={{ zIndex: 1050 }}>
                    <Spin size="large" tip="Loading occupancy history..." />
                </div>
            )}
            <div className="content container-fluid">
                {/* Page Header */}
                <div className="page-header mb-4">
                    <div className="row align-items-center">
                        <div className="col">
                            <h3 className="page-title text-dark fw-bold">Allotment & Vacation History</h3>
                            <ul className="breadcrumb bg-transparent p-0 mt-1">
                                <li className="breadcrumb-item">
                                    <Link to={routes.staffQuarter.inventory.dashboard}>Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item active">History</li>
                            </ul>
                        </div>
                        <div className="col-auto float-end ms-auto">
                            <button className="btn btn-light border shadow-sm">
                                <i className="ti ti-download me-1"></i> Export Log
                            </button>
                        </div>
                    </div>
                </div>

                {/* DASHBOARD SECTIONS */}
                {dashboardSections.map((section, sIdx) => (
                    <DashboardSection key={sIdx} title={section.title}>
                        {section.tiles.map((tile, tIdx) => (
                            <DashboardTile
                                key={tIdx}
                                title={tile.title}
                                icon={tile.icon}
                                count={tile.count}
                                link={tile.link}
                                colorClass={tile.colorClass}
                            />
                        ))}
                    </DashboardSection>
                ))}

                {/* FILTERS */}
                <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '12px' }}>
                    <div className="card-body p-3">
                        <div className="row g-3 align-items-end">
                            <div className="col-md-4">
                                <label className="form-label small text-muted fw-bold">Search</label>
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text bg-white border-end-0"><i className="ti ti-search text-muted"></i></span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0 ps-0"
                                        placeholder="Emp ID, Name, Location..."
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="col-md-3">
                                <label className="form-label small text-muted fw-bold">Status</label>
                                <select
                                    className="form-select form-select-sm"
                                    value={selectedStatus}
                                    onChange={(e) => setSelectedStatus(e.target.value)}
                                >
                                    {filterOptions.statuses.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div className="col-md-3">
                                <label className="form-label small text-muted fw-bold">Location</label>
                                <select
                                    className="form-select form-select-sm"
                                    value={selectedLocation}
                                    onChange={(e) => setSelectedLocation(e.target.value)}
                                >
                                    {filterOptions.locations.map(l => <option key={l} value={l}>{l}</option>)}
                                </select>
                            </div>
                            <div className="col-md-2 text-end">
                                <button
                                    className="btn btn-sm btn-light border text-muted w-100"
                                    onClick={() => {
                                        setSearchText("");
                                        setSelectedStatus("All Statuses");
                                        setSelectedLocation("All Locations");
                                    }}
                                >
                                    <i className="ti ti-x me-1"></i> Clear
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* TABLE */}
                <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                    <div className="card-header bg-white py-3 border-bottom-0" style={{ borderRadius: '12px 12px 0 0' }}>
                        <h5 className="card-title mb-0">Audit Trail & Compliance Records</h5>
                    </div>
                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover table-center mb-0">
                                <thead className="thead-light">
                                    <tr>
                                        {columns.map((col, idx) => (
                                            <th key={idx} className={col.className || ""}>{col.title}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredData.map((record, idx) => (
                                        <tr key={idx}>
                                            {columns.map((col, cIdx) => (
                                                <td key={cIdx} className={col.className || ""}>
                                                    {col.render ? col.render(getValueByPath(record, col.key), record, idx) : getValueByPath(record, col.key)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                    {filteredData.length === 0 && (
                                        <tr><td colSpan={columns.length} className="text-center py-5 text-muted">
                                            <div className="d-flex flex-column align-items-center">
                                                <i className="ti ti-search fs-1 mb-2 text-light"></i>
                                                <span>No history records found matching your filters.</span>
                                            </div>
                                        </td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

            </div>
            <style>{`
          .bg-success-light { background-color: rgba(25, 135, 84, 0.1); }
          .bg-danger-light { background-color: rgba(220, 53, 69, 0.1); }
          .bg-info-light { background-color: rgba(13, 202, 240, 0.1); }
          .bg-primary-light { background-color: rgba(var(--bs-primary-rgb), 0.1); }
          .hover-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 20px rgba(0,0,0,0.08) !important;
          }
      `}</style>
        </div>
    );
};

export default InventoryHistory;
